import React, { ReactElement } from "react";
import { TFunction } from "i18next";

import { TypeURL, URLS } from "@/constants/urls";
import DmriPolices from "@/views/PoliceDmri/Polices";
import DmriDetails from "views/PoliceDmri/Details";
import PnoPolices from "views/PolicePno/Polices";
import PnoSouscription from "views/PolicePno/Souscription";
import PnoSouscriptionsEnCours from "@/views/PolicePno/SouscriptionsEnCours";
import MrhPolices from "@/views/PoliceMrh";
import DpjPolices from "@/views/PolicePJ";
import DoPolices from "@/views/PoliceDo";
import PaiementsPlanifies from "@/views/TermPolicies";

export type MenuItem = MenuItemParent | MenuItemEnfant;
export interface MenuItemParent {
  type: "parent";
  sideBarIcon: string;
  sidebarLabel: string;
  subItems: MenuItem[];
}
export interface MenuItemEnfant {
  link: TypeURL;
  sidebarLabel: string | null;
  sideBarIcon: string | null;
  sidebarShow: boolean;
  titre?: string;
  pageDescription?: string;
  component: ReactElement;
}

/**
 * Cette configuration définit la structure complète du menu de l’espace professionnel.
 * Elle sert à :
 * - Définir les pages accessibles dans l’application (via leurs composants React associés)
 * - Configurer les URLs utilisées dans le système de routage dans react-router-dom
 * - Spécifier les éléments affichés dans la sidebar (icônes, libellés, visibilité, hiérarchie)
 * - Spécifier le titre qui sera affiché en haut de page
 *
 * Le menu de la sidebar est organisé en deux niveaux :
 * - `MenuItemParent`: représente une section principale du menu contenant plusieurs sous-éléments.
 * - `MenuItemEnfant` : représente une page ou un sous-menu accessible directement depuis la sidebar.
 * Chaque élément enfant peut également définir un titre et une description de page pour une meilleure
 * intégration dans le système de navigation et les métadonnées de l’interface.
 */
export const pagesConfig = (
  t: TFunction<"translation", undefined>,
  config: any
): MenuItem[] => [
  {
    link: URLS.BOARD,
    sidebarLabel: t("dashboard.menu.dashboard"),
    sideBarIcon: "fas fa-border-all",
    sidebarShow: config.SIDEBAR_SHOW_BOARD === "true",
    titre: t("dashboard.menu.dashboard"),
    component: <></>,
  },

  {
    type: "parent",
    sidebarLabel: t("dashboard.police.copro"),
    sideBarIcon: "fas fa-city",
    subItems: [
      {
        type: "parent",
        sidebarLabel: t("dashboard.menu.do"),
        sideBarIcon: "fas fa-tools",
        subItems: [
          {
            link: URLS.DO,
            sidebarLabel: t("dashboard.menu.contracts"),
            sideBarIcon: "fas fa-folder",
            sidebarShow: config.SIDEBAR_SHOW_DO === "true",
            titre: t("dashboard.menu.doContracts"),

            component: <DoPolices />,
          },
        ],
      },
      {
        type: "parent",
        //link: URLS.MRI,
        sidebarLabel: t("dashboard.menu.mri"),
        sideBarIcon: "fas fa-building",
        subItems: [
          {
            link: URLS.MRI_POLICES,
            sidebarLabel: t("dashboard.menu.contracts"),
            sideBarIcon: "fas fa-folder",
            sidebarShow: config.SIDEBAR_SHOW_MRI_CONTRACTS === "true",
            titre: t("common.dmri.multiriskBuildingInsurance"),
            component: <DmriPolices />,
          },
          {
            link: URLS.MRI_POLICE_DETAILS,
            sidebarLabel: null,
            sideBarIcon: null,
            sidebarShow: false,
            titre: t("common.dmri.multiriskBuildingInsurance"),
            component: <DmriDetails />,
          },
          {
            link: URLS.MRI_DEVIS,
            sidebarLabel: t("dashboard.menu.quotation"),
            sideBarIcon: "fas fa-file",
            sidebarShow: config.SIDEBAR_SHOW_MRI_DEVIS === "true",
            titre: t("common.dmri.multiriskBuildingInsurance"),
            component: <></>,
          },
        ],
      },
      {
        type: "parent",
        sidebarLabel: t("dashboard.menu.pj"),
        sideBarIcon: "fas fa-university",
        subItems: [
          {
            link: URLS.PJ,
            sidebarLabel: t("dashboard.menu.contracts"),
            sideBarIcon: "fas fa-folder",
            sidebarShow: config.SIDEBAR_SHOW_PJ === "true",
            titre: t("dashboard.menu.pjContracts"),
            component: <DpjPolices />,
          },
        ],
      },
    ],
  },
  {
    type: "parent",
    sidebarLabel: t("dashboard.menu.gestionLocative"),
    sideBarIcon: "fas fa-building",
    subItems: [
      {
        type: "parent",
        sidebarLabel: t("dashboard.menu.mrh"),
        sideBarIcon: "fas fa-home",
        subItems: [
          {
            link: URLS.MRH,
            sidebarLabel: t("dashboard.menu.contracts"),
            sideBarIcon: "fas fa-folder",
            sidebarShow: config.SIDEBAR_SHOW_H === "true",
            titre: t("dashboard.menu.homeInsurancePolicy"),
            component: <MrhPolices />,
          },
        ],
      },
      {
        type: "parent",
        //link: URLS.PNO,
        sidebarLabel: t("dashboard.menu.pno"),
        sideBarIcon: "fas fa-home",
        subItems: [
          {
            link: URLS.PNO_CONTRACTS,
            sidebarLabel: t("dashboard.menu.contracts"),
            sideBarIcon: "fas fa-folder",
            sidebarShow: config.SIDEBAR_SHOW_PNO_CONTRACTS === "true",
            titre: t("dashboard.menu.pnoContracts"),
            component: <PnoPolices />,
          },
          {
            link: URLS.PNO_SOUSCRIPTION,
            sidebarLabel: t("dashboard.menu.newContract"),
            sideBarIcon: "fas fa-plus-circle",
            sidebarShow: config.SIDEBAR_SHOW_PNO_NEW_CONTRACTS === "true",
            titre: t("dashboard.menu.newContract"),
            pageDescription: t("dashboard.menu.souscriptionCompte2"),
            component: <PnoSouscription />,
          },
          {
            link: URLS.PNO_SOUSCRIPTIONS_EN_COURS,
            sidebarLabel: t("dashboard.menu.souscriptionEnCours"),
            sideBarIcon: "fas fa-clipboard",
            sidebarShow: config.SIDEBAR_SHOW_PNO_SOUSCRIPTIONS === "true",
            titre: t("dashboard.menu.souscriptionEnCours"),
            component: <PnoSouscriptionsEnCours />,
          },
          {
            link: URLS.PNO_SINISTRES,
            sidebarLabel: t("dashboard.menu.ongoingClaims"),
            sideBarIcon: "fas fa-bolt",
            sidebarShow: config.SIDEBAR_SHOW_PNO_SINISTRES === "true",
            titre: t("dashboard.menu.ongoingClaims"),
            component: <></>,
          },
          {
            link: URLS.PNO_QUITTANCES,
            sidebarLabel: t("dashboard.menu.ongoingPremiums"),
            sideBarIcon: "fas fa-file",
            sidebarShow: config.SIDEBAR_SHOW_PNO_QUITTANCES === "true",
            titre: t("dashboard.menu.ongoingPremiums"),
            component: <></>,
          },
        ],
      },
    ],
  },
  {
    type: "parent",
    sidebarLabel: t("dashboard.menu.comptabilite"),
    sideBarIcon: "fas fa-euro-sign",
    subItems: [
      {
        link: URLS.PAIEMENT_PLANIFIES,
        sidebarLabel: t("dashboard.menu.plannedPayments"),
        sideBarIcon: "fas fa-calendar-check",
        sidebarShow: config.SIDEBAR_SHOW_TERMPOLICIES === "true",
        titre: t("dashboard.menu.plannedPayments"),
        component: <PaiementsPlanifies />,
      },
    ],
  },
  /*{
    link: URLS.SINISTRES,
    sidebarLabel: t("dashboard.menu.claims"),
    sideBarIcon: "fas fa-bolt",
    sidebarShow: config.SIDEBAR_SHOW_CLAIMS === "true",
    titre: t("dashboard.menu.claims"),
    component: <></>,
  },*/
  // {
  //   id: 'contractsPerMonth',
  //   link: '/contrats',
  //   label: t('dashboard.menu.contractsPerMonth'),
  //   icon: 'fas fa-folder',
  // },
];
